<template>
  <div id="top" class="finance-page-shell supervisor-page-shell">
    <LoadingDiv v-if="!isReady" />

    <template v-else>
      <div class="supervisor-intro">
        <UiPageHeader
          title="Finance"
          description="Process claim payments, approve invoices, and manage finance account updates from one focused workspace."
        />
      </div>

      <div class="supervisor-layout">
        <nav class="supervisor-sidebar" aria-label="Finance sections">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            class="supervisor-nav-item"
            :class="{ 'is-active': currTab?.id === tab.id }"
            :aria-current="currTab?.id === tab.id ? 'page' : undefined"
            aria-controls="finance-workspace"
            @click="handleActive(tab)"
          >
            <Banknote v-if="tab.title === 'Claims Payment'" :size="18" aria-hidden="true" />
            <ReceiptText v-else-if="tab.title === 'Invoices Approval'" :size="18" aria-hidden="true" />
            <Landmark v-else :size="18" aria-hidden="true" />
            <span>{{ navigationLabel(tab) }}</span>
          </button>
        </nav>

        <section
          id="finance-workspace"
          class="supervisor-content finance-content"
          :aria-label="currTab?.title || 'Finance workspace'"
        >
          <div class="supervisor-workbench finance-workbench">
            <div class="supervisor-view-shell finance-view-shell sba-slide-in" :key="currTab?.id">
              <ClaimsDiv
                v-if="currTab?.title === 'Claims Payment'"
                :resetKey="claimsResetKey"
              />

              <InvoicesDiv
                v-if="currTab?.title === 'Invoices Approval'"
                :resetKey="invoicesResetKey"
              />

              <AccountUpdateDiv
                v-if="currTab?.title === 'Accounts'"
                :resetKey="accountsResetKey"
              />
            </div>
          </div>
        </section>
      </div>
    </template>
  </div>
</template>

<script>
import { onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { Banknote, Landmark, ReceiptText } from "@lucide/vue";
import useModuleNavigation from "@/composables/useModuleNavigation";
import LoadingDiv from "@/components/LoadingDiv.vue";
import InvoicesDiv from "@/components/finance/FinanceInvoicesComponent.vue";
import ClaimsDiv from "@/components/finance/FinanceClaimsComponent.vue";
import AccountUpdateDiv from "@/components/finance/FinanceAccountsUpdateComponent.vue";
import UiPageHeader from "@/ui/components/UiPageHeader.vue";

export default {
  name: "financeHome",
  components: {
    AccountUpdateDiv,
    Banknote,
    ClaimsDiv,
    InvoicesDiv,
    Landmark,
    LoadingDiv,
    ReceiptText,
    UiPageHeader,
  },
  setup() {
    const route = useRoute();
    const isReady = ref(true);
    const currTab = ref(null);
    const claimsResetKey = ref(0);
    const invoicesResetKey = ref(0);
    const accountsResetKey = ref(0);
    const tabs = ref([]);
    const { activateLocalTab } = useModuleNavigation();

    const routeSections = {
      "claims-payment": "Claims Payment",
      "invoices-approval": "Invoices Approval",
      accounts: "Accounts",
    };

    onMounted(() => {
      document.title = "Finance";

      tabs.value.push({ id: 1, name: "Finance", title: "Claims Payment", icon: null, url: null, hash: null });
      tabs.value.push({ id: 2, name: "Finance", title: "Invoices Approval", icon: null, url: null, hash: null });
      tabs.value.push({ id: 3, name: "Finance", title: "Accounts", icon: null, url: null, hash: null });

      currTab.value = tabs.value.find((tab) => tab.title === "Claims Payment");
      activateRouteSection();
    });

    const handleActive = (tab) => {
      if (tab?.title === "Claims Payment") {
        claimsResetKey.value++;
      }

      if (tab?.title === "Invoices Approval") {
        invoicesResetKey.value++;
      }

      if (tab?.title === "Accounts") {
        accountsResetKey.value++;
      }

      activateLocalTab(tab, currTab);
    };

    const navigationLabel = (tab) => {
      const labels = {
        "Claims Payment": "Claims",
        "Invoices Approval": "Invoices",
      };

      return labels[tab?.title] || tab?.title;
    };

    function activateRouteSection() {
      const targetTitle = routeSections[route.query.section];
      const targetTab = tabs.value.find((tab) => tab.title === targetTitle);

      if (targetTab && currTab.value?.id !== targetTab.id) {
        handleActive(targetTab);
      }
    }

    watch(
      () => route.query.section,
      () => activateRouteSection()
    );

    return {
      accountsResetKey,
      claimsResetKey,
      currTab,
      handleActive,
      invoicesResetKey,
      isReady,
      navigationLabel,
      tabs,
    };
  },
};
</script>

<style scoped>
.supervisor-page-shell {
  box-sizing: border-box;
  margin: 0 auto;
  max-width: var(--ui-content-width);
  padding: 2rem 2rem 3rem;
  width: 100%;
}

.supervisor-intro {
  border-bottom: 1px solid var(--ui-border);
  margin-bottom: 2.5rem;
  padding-bottom: 1.75rem;
}

.supervisor-intro :deep(.ui-page-header) {
  margin-bottom: 0;
}

.supervisor-intro :deep(.ui-page-header__title) {
  font-size: clamp(1.75rem, 2.1vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.025em;
}

.supervisor-intro :deep(.ui-page-header__description) {
  font-size: 1rem;
  line-height: 1.55;
}

.supervisor-layout {
  align-items: start;
  display: grid;
  gap: 3.5rem;
  grid-template-columns: 15rem minmax(0, 1fr);
}

.supervisor-sidebar {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.supervisor-nav-item {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: calc(var(--ui-radius) - 2px);
  box-shadow: none;
  color: var(--ui-foreground);
  cursor: pointer;
  display: grid;
  font-family: var(--ui-font-sans);
  font-size: 0.875rem;
  font-weight: 500;
  gap: 0.75rem;
  grid-template-columns: 1.25rem minmax(0, 1fr);
  min-height: 2.75rem;
  padding: 0.6rem 0.75rem;
  text-align: left;
  width: 100%;
}

.supervisor-nav-item:hover,
.supervisor-nav-item.is-active {
  background: var(--ui-muted);
  color: var(--ui-foreground);
}

.supervisor-nav-item.is-active {
  font-weight: 600;
}

.supervisor-nav-item:focus-visible {
  outline: 2px solid var(--ui-ring);
  outline-offset: 2px;
}

.supervisor-content,
.supervisor-workbench,
.supervisor-view-shell {
  background: transparent;
  border: 0;
  box-shadow: none;
  min-width: 0;
}

.supervisor-view-shell :deep(.queue-title-block label),
.supervisor-view-shell :deep(.sba-open-title label) {
  display: none;
}

.supervisor-view-shell :deep(.premium-queue-head),
.supervisor-view-shell :deep(.payment-batch-head),
.supervisor-view-shell :deep(.claim-batch-row),
.supervisor-view-shell :deep(.payment-empty-state),
.supervisor-view-shell :deep(.finance-batch-detail),
.supervisor-view-shell :deep(.batch-detail-head),
.supervisor-view-shell :deep(.batch-table-wrap),
.supervisor-view-shell :deep(.batch-action-strip),
.supervisor-view-shell :deep(.invoice-review-head),
.supervisor-view-shell :deep(.invoice-list-card),
.supervisor-view-shell :deep(.invoice-row-card),
.supervisor-view-shell :deep(.finance-approval-card),
.supervisor-view-shell :deep(.accounts-review-head),
.supervisor-view-shell :deep(.accounts-list-card),
.supervisor-view-shell :deep(.account-row-card),
.supervisor-view-shell :deep(.accounts-empty-state) {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

@media (max-width: 1100px) {
  .supervisor-page-shell {
    padding: 1.5rem 1.5rem 2.5rem;
  }

  .supervisor-layout {
    gap: 2.5rem;
  }
}

@media (max-width: 900px) {
  .supervisor-layout {
    gap: 2rem;
    grid-template-columns: minmax(0, 1fr);
  }

  .supervisor-sidebar {
    flex-direction: row;
    max-width: 100%;
    overflow-x: auto;
    padding-bottom: 0.25rem;
    scrollbar-width: none;
  }

  .supervisor-sidebar::-webkit-scrollbar {
    display: none;
  }

  .supervisor-nav-item {
    flex: 0 0 auto;
    min-width: max-content;
    width: auto;
  }
}

@media (max-width: 767px) {
  .supervisor-page-shell {
    padding: 1.25rem 1rem 2rem;
  }

  .supervisor-intro {
    margin-bottom: 1.5rem;
    padding-bottom: 1.25rem;
  }

  .supervisor-layout {
    gap: 1.5rem;
  }

  .supervisor-nav-item {
    min-height: 2.75rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .supervisor-view-shell :deep(*) {
    animation: none !important;
  }
}
</style>
